import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { fileReviewAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import './FileReviewTemplate.css';

/** Staff members is the first Settings nav item — safe default vs tenant-wide config. */
const settingsStaffListPath = (tenantPath) => tenantPath('/settings/staff');

function FileReviewTemplate() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const settingsBack = settingsStaffListPath(tenantPath);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [edits, setEdits] = useState({});
  const editsPendingRef = useRef(false);
  const [newRowForm, setNewRowForm] = useState({
    question_text: '',
    initial_na: false,
    annual1_na: false,
    annual2_na: false,
    is_active: true,
  });

  useEffect(() => {
    if (!hasPermission('file_reviews.manage_templates')) {
      navigate(tenantPath('/settings'), { replace: true });
      return;
    }
    loadTemplate();
  }, [navigate, tenantPath]);

  const loadTemplate = async () => {
    setLoading(true);
    setError(null);
    const timeoutMs = 15000;
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out. Check that the backend is running and that File Review is enabled for this tenant.')), timeoutMs);
    });
    try {
      const res = await Promise.race([fileReviewAPI.getTemplate(), timeoutPromise]);
      setRows(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load template';
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const setEdit = (id, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const getValue = (row, field) => {
    const edit = edits[row.id];
    if (edit && field in edit) return edit[field];
    return row[field];
  };

  const buildRowBody = (edit) => {
    const body = {};
    if ('question_text' in edit) body.question_text = edit.question_text;
    if ('initial_na' in edit) body.initial_na = edit.initial_na;
    if ('annual1_na' in edit) body.annual1_na = edit.annual1_na;
    if ('annual2_na' in edit) body.annual2_na = edit.annual2_na;
    if ('is_active' in edit) body.is_active = edit.is_active;
    if ('sort_order' in edit) body.sort_order = edit.sort_order;
    return body;
  };

  const hasUnsavedEdits = Object.keys(edits).length > 0;

  // Auto-save when edits change (debounced)
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    editsPendingRef.current = true;
    const t = setTimeout(async () => {
      setSaving(true);
      setSaveMessage(null);
      try {
        for (const rowId of Object.keys(edits)) {
          const edit = edits[rowId];
          if (!edit || Object.keys(edit).length === 0) continue;
          const row = rows.find((r) => String(r.id) === String(rowId));
          if (!row) continue;
          const body = buildRowBody(edit);
          await fileReviewAPI.updateTemplateRow(row.id, body);
        }
        setEdits({});
        editsPendingRef.current = false;
        await loadTemplate();
        setSaveMessage('Saved');
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (err) {
        editsPendingRef.current = false;
        setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
        setTimeout(() => setSaveMessage(null), 3000);
      } finally {
        setSaving(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [edits]);

  const handleMoveUp = async (row) => {
    const sorted = rows.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = sorted.findIndex((r) => r.id === row.id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    const thisOrder = row.sort_order ?? idx;
    const prevOrder = prev.sort_order ?? idx - 1;
    setSaving(true);
    setSaveMessage(null);
    try {
      await Promise.all([
        fileReviewAPI.updateTemplateRow(row.id, { sort_order: prevOrder }),
        fileReviewAPI.updateTemplateRow(prev.id, { sort_order: thisOrder }),
      ]);
      await loadTemplate();
      setSaveMessage('Order updated.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to reorder');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveDown = async (row) => {
    const sorted = rows.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = sorted.findIndex((r) => r.id === row.id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    const thisOrder = row.sort_order ?? idx;
    const nextOrder = next.sort_order ?? idx + 1;
    setSaving(true);
    setSaveMessage(null);
    try {
      await Promise.all([
        fileReviewAPI.updateTemplateRow(row.id, { sort_order: nextOrder }),
        fileReviewAPI.updateTemplateRow(next.id, { sort_order: thisOrder }),
      ]);
      await loadTemplate();
      setSaveMessage('Order updated.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to reorder');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRow = async () => {
    const text = newRowForm.question_text?.trim();
    if (!text) {
      setSaveMessage('Question text is required.');
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      await fileReviewAPI.createTemplateRow({
        question_text: text,
        initial_na: !!newRowForm.initial_na,
        annual1_na: !!newRowForm.annual1_na,
        annual2_na: !!newRowForm.annual2_na,
        is_active: newRowForm.is_active !== false,
      });
      setNewRowForm({
        question_text: '',
        initial_na: false,
        annual1_na: false,
        annual2_na: false,
        is_active: true,
      });
      await loadTemplate();
      setSaveMessage('Row added.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to add row');
    } finally {
      setSaving(false);
    }
  };

  const sortedRows = rows.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  if (loading && rows.length === 0) {
    return (
      <div className="file-review-template-page">
        <ContentHeader
          title="File Review Template"
          backTo={settingsBack}
          backLabel="Back to Settings"
        />
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  if (error && rows.length === 0) {
    return (
      <div className="file-review-template-page">
        <ContentHeader
          title="File Review Template"
          backTo={settingsBack}
          backLabel="Back to Settings"
        />
        <p className="error-msg">{error}</p>
        <p className="error-hint">If the feature was just enabled, refresh the page. Ensure the File Review migration and permissions migration have been run on the backend.</p>
      </div>
    );
  }

  return (
    <div className="file-review-template-page">
      <ContentHeader
        title="File Review Template"
        titleHint="Edit questions and section headers for file reviews. Use up/down arrows to reorder. Use the Active checkbox to show or hide questions (inactive rows are hidden from new reviews). Rows where all three NA checkboxes are checked are treated as section headers. Changes apply only to new reviews; existing reviews keep their snapshot. Changes save automatically after you edit—use Back to Settings (top or bottom) or pick another item in the Settings sidebar when you are done."
        backTo={settingsBack}
        backLabel="Back to Settings"
        actions={
          saveMessage && (
            <span className={saveMessage.startsWith('Failed') ? 'file-review-template-status error' : 'file-review-template-status'}>
              {saveMessage}
            </span>
          )
        }
      />
      {saveMessage && <p className={saveMessage.startsWith('Failed') ? 'error-msg' : 'save-msg'}>{saveMessage}</p>}
      <div className="file-review-template-table-wrap">
        <table className="file-review-template-table">
          <thead>
            <tr>
              <th className="col-order">Order</th>
              <th>Question / Section text</th>
              <th className="col-na">Initial NA</th>
              <th className="col-na">Annual1 NA</th>
              <th className="col-na">Annual2 NA</th>
              <th className="col-active">Active</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={row.id} className={row.is_active === false ? 'row-inactive' : ''}>
                <td className="col-order">
                  <div className="order-buttons">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(row)}
                      disabled={saving || index === 0}
                      className="btn-order"
                      title="Move up"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveDown(row)}
                      disabled={saving || index === sortedRows.length - 1}
                      className="btn-order"
                      title="Move down"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td>
                  <textarea
                    value={getValue(row, 'question_text') ?? ''}
                    onChange={(e) => setEdit(row.id, 'question_text', e.target.value)}
                    className="input-textarea question-text full-width"
                    rows={2}
                    placeholder="Question or section header"
                  />
                </td>
                <td className="col-na">
                  <input
                    type="checkbox"
                    checked={!!getValue(row, 'initial_na')}
                    onChange={(e) => setEdit(row.id, 'initial_na', e.target.checked)}
                  />
                </td>
                <td className="col-na">
                  <input
                    type="checkbox"
                    checked={!!getValue(row, 'annual1_na')}
                    onChange={(e) => setEdit(row.id, 'annual1_na', e.target.checked)}
                  />
                </td>
                <td className="col-na">
                  <input
                    type="checkbox"
                    checked={!!getValue(row, 'annual2_na')}
                    onChange={(e) => setEdit(row.id, 'annual2_na', e.target.checked)}
                  />
                </td>
                <td className="col-active">
                  <input
                    type="checkbox"
                    checked={getValue(row, 'is_active') !== false}
                    onChange={(e) => setEdit(row.id, 'is_active', e.target.checked)}
                  />
                </td>
              </tr>
            ))}
            <tr className="new-row">
              <td className="col-order">
                <button
                  type="button"
                  onClick={handleAddRow}
                  disabled={saving || !newRowForm.question_text?.trim()}
                  className="btn-add-row"
                >
                  Add
                </button>
              </td>
              <td>
                <textarea
                  value={newRowForm.question_text}
                  onChange={(e) => setNewRowForm((f) => ({ ...f, question_text: e.target.value }))}
                  className="input-textarea question-text full-width"
                  rows={2}
                  placeholder="New question or section header"
                />
              </td>
              <td className="col-na">
                <input
                  type="checkbox"
                  checked={!!newRowForm.initial_na}
                  onChange={(e) => setNewRowForm((f) => ({ ...f, initial_na: e.target.checked }))}
                />
              </td>
              <td className="col-na">
                <input
                  type="checkbox"
                  checked={!!newRowForm.annual1_na}
                  onChange={(e) => setNewRowForm((f) => ({ ...f, annual1_na: e.target.checked }))}
                />
              </td>
              <td className="col-na">
                <input
                  type="checkbox"
                  checked={!!newRowForm.annual2_na}
                  onChange={(e) => setNewRowForm((f) => ({ ...f, annual2_na: e.target.checked }))}
                />
              </td>
              <td className="col-active">
                <input
                  type="checkbox"
                  checked={newRowForm.is_active !== false}
                  onChange={(e) => setNewRowForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="file-review-template-footer-actions">
        <Link to={settingsBack} className="btn-cancel">
          Back to Settings
        </Link>
      </div>
    </div>
  );
}

export default FileReviewTemplate;
